// ─── IMAGE UPLOAD ────────────────────────────────────────────────────────────
// Admin picks a file → resize to a 1000×1000 JPEG with white padding (so any
// aspect ratio displays fully in every product thumbnail — 140×140, 80×80,
// 40×40 — without cropping) → upload to Supabase Storage → return public URL.
//
// Why square-pad instead of just resize?
//   Every display site uses a square container with `object-fit: cover`. Cover
//   CROPS non-square images. Square-padding on upload means every stored image
//   is already the right shape, so cover never cuts anything off. One upload,
//   looks right everywhere.
//
// Output: ~80–250 KB per image. Well within the 5MB default bucket limit.

import { supabase } from './supabase';

const BUCKET = 'product-images';
const DEFAULT_OUTPUT = 1000; // square output size
const DEFAULT_QUALITY = 0.85;

/** Load a File into an HTMLImageElement. Revokes the object URL on settle. */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Couldn't read that image. Try a JPG or PNG.")); };
    img.src = url;
  });
}

/** Draw the image centered on a square canvas with white padding.
 *  Returns a JPEG Blob at the requested quality. */
async function resizeToSquareBlob(file, outputSize = DEFAULT_OUTPUT, quality = DEFAULT_QUALITY) {
  const img = await loadImage(file);
  // Scale so the image fits INSIDE outputSize preserving aspect. If the image
  // is already smaller than outputSize, don't upscale — just draw it centered.
  const scale = Math.min(outputSize / img.width, outputSize / img.height, 1);
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');
  if(!ctx) throw new Error("Your browser doesn't support canvas.");
  // White square background so transparent-background inputs still export as
  // flat JPEG without black fill. If a future admin wants PNG transparency,
  // switch the output mime to 'image/png' and remove these two lines.
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, outputSize, outputSize);
  // Center
  const x = Math.round((outputSize - w) / 2);
  const y = Math.round((outputSize - h) / 2);
  ctx.drawImage(img, x, y, w, h);
  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error("Image encoding failed.")),
      'image/jpeg',
      quality
    );
  });
}

/** Pick a random, URL-safe filename that won't collide on fast double-uploads. */
function randomName(ext = 'jpg') {
  const ts = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  return `${ts}-${rand}.${ext}`;
}

/** Entry point: upload a user-selected File and return a public URL.
 *  @param {File}   file        From an <input type="file"> picker.
 *  @param {object} [opts]
 *  @param {string} [opts.folder='products']  Subpath inside the bucket so
 *                                            products / kits / materials
 *                                            stay organized.
 *  @param {(pct:number)=>void} [opts.onProgress]  0-100 progress callback.
 *  @returns {Promise<string>}  Public URL ready to store in `imageUrl`.
 */
/** Upload a user's profile avatar. Stored at avatars/{userId}/{ts}.jpg inside
 *  the shared product-images bucket. See migration 0009 for the RLS policy
 *  that lets authenticated users write to their own uid path.
 *
 *  Avatars are displayed at 28–56px in the UI so we resize to a smaller
 *  square (400×400) — output ~20-60KB instead of the 1000×1000 used for
 *  product shots. Uses upsert:true so re-uploading replaces the old file
 *  for the same user in the same path.
 *
 *  @param {File}   file
 *  @param {string} userId  auth.uid() of the signed-in user.
 *  @returns {Promise<string>}  Public URL.
 */
export async function uploadAvatar(file, userId) {
  if(!userId) throw new Error("You need to be signed in to change your avatar.");
  if(!file) throw new Error("Pick a file first.");
  if(!/^image\//.test(file.type || "")) throw new Error("That's not an image file. Try JPG or PNG.");
  if(file.size > 20 * 1024 * 1024) throw new Error("Image is too large (20MB max). Try a smaller photo.");
  const blob = await resizeToSquareBlob(file, 400, 0.85);
  const path = `avatars/${userId}/${Date.now()}.jpg`;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: 'image/jpeg', cacheControl: '86400', upsert: true });
  if(error) {
    if(/bucket not found/i.test(error.message || "")) {
      throw new Error("Storage bucket missing. Run migration 0008.");
    }
    if(/row.level security|not.authorized/i.test(error.message || "")) {
      throw new Error("Not allowed to upload avatar. Run migration 0009 to enable per-user avatar uploads.");
    }
    throw error;
  }
  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(data.path);
  return urlData?.publicUrl || "";
}

export async function uploadProductImage(file, opts = {}) {
  if(!file) throw new Error("Pick a file first.");
  if(!/^image\//.test(file.type || "")) throw new Error("That's not an image file. Try JPG or PNG.");
  if(file.size > 20 * 1024 * 1024) throw new Error("Image is too large (20MB max). Try a smaller photo.");
  const folder = opts.folder || 'products';
  opts.onProgress?.(10);
  const blob = await resizeToSquareBlob(file);
  opts.onProgress?.(50);
  const path = `${folder}/${randomName('jpg')}`;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: 'image/jpeg', cacheControl: '31536000', upsert: false });
  if(error) {
    // Surface RLS / bucket issues clearly instead of the raw JS-SDK wrap.
    if(/bucket not found/i.test(error.message || "")) {
      throw new Error("The product-images storage bucket is missing. Run migration 0008.");
    }
    if(/row.level security|not.authorized/i.test(error.message || "")) {
      throw new Error("Not allowed to upload. Only admins can add product images.");
    }
    throw error;
  }
  opts.onProgress?.(95);
  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(data.path);
  opts.onProgress?.(100);
  return urlData?.publicUrl || "";
}
